import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import type { Request } from 'express';

import getMatchedRoute from '@rover/react-app/src/server/renderPage/getMatchedRoute';
import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiCountryConfigurationNavigationRetrieve,
  getApiCountryConfigurationNavigationRetrieveQueryKey,
  useApiCountryConfigurationNavigationRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-country-configuration-navigation-retrieve';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
  useApiFrontendConfigurationRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiFrontendServiceTypesList,
  getApiFrontendServiceTypesListQueryKey,
  useApiFrontendServiceTypesList,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import {
  apiSearchOptimizedRetrieve,
  getApiSearchOptimizedRetrieveQueryKey,
  useApiSearchOptimizedRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-search-optimized-retrieve';
import { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http500Error } from '@rover/utilities/HttpError';
import { filterNullishProperties } from '@rover/utilities/object';
import { parseSeoLocationFromSegments } from '@rover/utilities/seo';
import { redirect } from '@rover/utilities/url.server';

import MetaDiscountSmokeTest from './components/MetaDiscountSmokeTest';
import { handleSeoLocationQueryError } from './utils/handleSeoLocationQueryError';
import {
  getApiSearchSeoLocationFaqsListPrefetchQueryOptions,
  getApiSearchSeoLocationPrefetchQueryOptions,
  getApiSearchSeoLocationReviewsListPrefetchQueryOptions,
  useApiSearchSeoLocationRetrieve,
  useApiSearchSeoLocationReviewsList,
} from './utils/useSeoLocationHooks';
import SeoLocationPageTemplate from './SeoLocationPageTemplate';
import { getTopNeighborhoodServices } from './utils';

type RouteParams = {
  seoLocationSlug: string;
  countryCode?: string;
  languageCode?: string;
};

const SeoLocationPageContainer: FC & {
  reactQueryPrefetch: (queryClient: QueryClient, req: Request) => Promise<any>;
} = () => {
  const {
    seoLocationSlug = '',
    countryCode: countryUrlPrefix,
    languageCode,
  } = useParams<RouteParams>();
  const seoLocationDescriptor = parseSeoLocationFromSegments(
    countryUrlPrefix,
    languageCode,
    seoLocationSlug
  );

  const {
    data: seoLocation,
    isError: isErrorSeoLocation,
    isLoading: isLoadingSeoLocation,
    error: errorSeoLocation,
  } = useApiSearchSeoLocationRetrieve(seoLocationDescriptor);
  if (!seoLocation) throw new Http500Error();
  const {
    isLoading: isLoadingReviews,
    isError: isErrorReviews,
    data: reviews,
  } = useApiSearchSeoLocationReviewsList(seoLocationDescriptor);

  const {
    isLoading: isLoadingCountryConfig,
    isError: isErrorCountryConfig,
    data: countryConfig,
  } = useApiCountryConfigurationNavigationRetrieve();
  const {
    isLoading: isLoadingFrontendConfig,
    isError: isErrorFrontendConfig,
    data: frontendConfig,
  } = useApiFrontendConfigurationRetrieve();
  const {
    isLoading: isLoadingServices,
    isError: isErrorServices,
    data: serviceTypes,
  } = useApiFrontendServiceTypesList();

  const searchOptimizedRetrieveParams = filterNullishProperties(seoLocation.details.searchParams);
  const {
    isLoading: isLoadingSearch,
    isError: isErrorSearch,
    data: searchResponse,
    error: errorSearch,
  } = useApiSearchOptimizedRetrieve(searchOptimizedRetrieveParams);

  const isError =
    isErrorServices ||
    isErrorReviews ||
    isErrorCountryConfig ||
    isErrorFrontendConfig ||
    isErrorSeoLocation ||
    isErrorSearch;

  if (isError) {
    // 410s should be handled during prefetch, but just in case, also handle them here
    for (const error of [errorSeoLocation, errorSearch]) {
      if (error) {
        handleSeoLocationQueryError(error);
      }
    }

    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: {
        pathname: getPath(),
        isErrorServices,
        isErrorReviews,
        isErrorCountryConfig,
        isErrorFrontendConfig,
        isErrorSeoLocation,
        isErrorSearch,
      },
    });
    throw new Http500Error();
  }

  const isLoading =
    isLoadingServices ||
    isLoadingReviews ||
    isLoadingFrontendConfig ||
    isLoadingCountryConfig ||
    isLoadingSeoLocation ||
    isLoadingSearch;
  if (isLoading) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage(`SeoLocationPageContainer rendered with missing prefetches`, {
        tags: {
          pathname: getPath(),
          isLoadingServices,
          isLoadingReviews,
          isLoadingFrontendConfig,
          isLoadingCountryConfig,
          isLoadingSeoLocation,
          isLoadingSearch,
        },
      });
    }

    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  const topNeighborhoodServices = getTopNeighborhoodServices(seoLocation);

  // NOTE: This should never happen as the dog training slugs evaluate first in the seoRouteResolver
  if (seoLocation.details.serviceTypeSlug === ServiceTypeSlugEnum['dog-training']) {
    throw new Http500Error();
  }

  return (
    <>
      <MetaDiscountSmokeTest />
      <SeoLocationPageTemplate
        seoLocation={seoLocation}
        seoLocationDescriptor={seoLocationDescriptor}
        reviews={reviews.results!}
        countryConfig={countryConfig}
        frontendConfig={frontendConfig}
        serviceTypes={serviceTypes}
        topNeighborhoodServices={topNeighborhoodServices}
        searchResponse={searchResponse}
      />
    </>
  );
};

SeoLocationPageContainer.reactQueryPrefetch = async (queryClient: QueryClient, req: Request) => {
  const { pageRoute } = getMatchedRoute(req.path);
  // v6 `RouteMatch` flattens params onto the match itself — there's no nested
  // `.match` like there was in v5.
  const {
    seoLocationSlug,
    countryCode: countryUrlPrefix = 'us',
    languageCode,
  } = pageRoute?.params ?? {};
  const seoLocationDescriptor = parseSeoLocationFromSegments(
    countryUrlPrefix,
    languageCode,
    seoLocationSlug
  );

  let seoLocation: SeoLocation;
  try {
    seoLocation = await queryClient.fetchQuery(
      getApiSearchSeoLocationPrefetchQueryOptions(seoLocationDescriptor)
    );
  } catch (error) {
    return handleSeoLocationQueryError(error);
  }

  // redirect if the canonical url doesn't match the requested url.
  // This can happen with typos (`---` instead of `--`), or if the service type slug
  // is not translated (`dog-boarding` instead of `cuidadores-de-perros` for /es/).
  const url = new URL(seoLocation.details.url);
  if (req.path !== url.pathname) {
    url.search = req.originalUrl.split('?')[1] ?? '';
    redirect(url);
  }

  const searchOptimizedRetrieveParams = filterNullishProperties(seoLocation.details.searchParams);

  return Promise.all([
    queryClient
      .fetchQuery({
        queryKey: getApiSearchOptimizedRetrieveQueryKey(searchOptimizedRetrieveParams),
        queryFn: () => apiSearchOptimizedRetrieve(searchOptimizedRetrieveParams),
      })
      .catch(handleSeoLocationQueryError),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceTypesListQueryKey(),
      queryFn: () => apiFrontendServiceTypesList(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryConfigurationNavigationRetrieveQueryKey(),
      queryFn: () => apiCountryConfigurationNavigationRetrieve(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
      queryFn: () => apiFrontendConfigurationRetrieve(),
    }),
    queryClient.prefetchQuery(
      getApiSearchSeoLocationReviewsListPrefetchQueryOptions(seoLocationDescriptor)
    ),
    queryClient.prefetchQuery(
      getApiSearchSeoLocationFaqsListPrefetchQueryOptions(
        seoLocationDescriptor,
        seoLocation.localityType
      )
    ),
  ]);
};

export default SeoLocationPageContainer;
